<template>
    <section class="address-chooser flow__panel">
        <div class="address-chooser__main">
            <h2 class="address-chooser__title">
                {{ myResources.title }}
            </h2>
            <loading-animation v-if="loading"
                               class="address-chooser__waiting">
                {{ myResources.loadingMessage }}
            </loading-animation>
            <div v-else-if="badPostcode" class="address-chooser__error">
                <hh-form>
                    <hh-error-display class="address-chooser__error__message"
                                      :error="errorLoading" />
                    <hh-btn class="address-chooser__error__return-link"
                            icon-class="fas fa-map-signs"
                            icon-location="pre"
                            :text="myResources.btnReload"
                            @click="$router.push({ name: 'Welcome' })" />
                </hh-form>
            </div>
            <section v-else class="address-chooser__addresses">
                <div class="address-chooser__addresses__info">
                    Postcode: <strong>{{ postcode }}</strong> <router-link :to="{ name: 'Welcome' }">
            <!-- NOTE This innocuous comment stops the linter whining about the text being on the same line as the tag
              and prevents an underlined space being added before the text -->{{ myResources.linkChange }}
                    </router-link>
                </div>
                <div class="address-chooser__addresses__selection">
                    <label>
                        {{ myResources.lblAddress }}
                    </label>
                    <select v-model="selectedAddress"
                            class="address-chooser__addresses__list">
                        <option :value="null">
                            {{ myResources.selectEmptyMessage }}
                        </option>
                        <option v-for="item in addresses"
                                :key="item.key"
                                :value="item">
                            {{ _getAddressDisplay(item) }}
                        </option>
                    </select>
                </div>
                <hh-btn type="cta"
                        class="address-chooser__cta"
                        :text="commonResources.continue"
                        :disabled="!canProceed"
                        @click="onProceed" />
            </section>
        </div>
    </section>
</template>

<script>

import ProgressManager from '@/progress-manager'
import PropertyManager from '../property-manager'

import AnalyticsPage from './analytics-page'
import ResourcesHelper from '@/components/resources-helper'

import strings from '@/helpers/strings'

import LoadingAnimation from '@/components/app-core/loading-animation'

export default {

    mixins: [
        AnalyticsPage,
        ResourcesHelper,
    ],

    data() {
        return {
            postcode: this.$route.query? this.$route.query.postcode : null,
            loading: true,
            badPostcode: false,
            addresses: [],
            selectedAddress: null,
        }
    },

    async mounted() {
        try {
            await this._search()
        } catch (err) {
            throw err
        }
    },

    computed: {
        /**
         * Return true if required parts of the screen have been completed.
         * @return {boolean}
         */
        canProceed() {
            return this.selectedAddress !== null
        },

        /**
         * Return the resources object for this page.
         * @return {AddressChooserResources}
         */
        myResources() {
            return this.parseResources('views.addressChooser')
        },

        /**
         * Parse and return the loading error string.
         * @return {string}
         */
        errorLoading() {
            return strings.parse(this.myResources.errorLoading, this)
        },
    },

    methods: {

        /**
         * Load addresses for the given postcode.
         * @private
         */
        async _search() {
            try {
                this.addresses = await PropertyManager.loadAddressesForAsync(this.postcode)
                this.loading = false
                this.badPostcode = false
            } catch (err) {
                this.loading = false
                this.badPostcode = true
            }
        },

        /**
         * Gets the address display to show in the drop down list.
         * @param {{ address: string, id: int }} item
         * @returns {string}
         * @private
         */
        _getAddressDisplay(item) {
            return this.addresses.filter(a => a.address === item.address).length > 1
                ? `${ item.address } (${ item.id })`
                : item.address
        },

        /**
         * If an address has been chosen, save it and move on.
         */
        async onProceed() {
            if (!this.selectedAddress) return

            this.loading = true

            try {
                await PropertyManager.choosePropertyAsync(this.selectedAddress)

                this.trackEvent('address_search', 'address_chosen')
                this.$router.push(ProgressManager.getNext(this.$route.name, { id: this.selectedAddress.id }))
            } catch (err) {
                this.$logger.log(`Failed to address data: ${ this.selectedAddress.id }`, 'AddressChooser')
                this.loading = false
                this.badPostcode = true
            }
        },
    },

    components: {
        LoadingAnimation,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.

.address-chooser {
    @include element('error') {
        padding-bottom: 1em;

        @include element('return-link') {
            float: right;

            @include bp-min-small {
                position: relative;
                bottom: #{$inner-wizard-container-min-height - 475px};
            }

            @include bp-min-mid {
                position: relative;
                bottom: #{$inner-wizard-container-min-height - 470px};
            }
        }
    }

    @include element('addresses') {
        @include element('selection') {
            margin: 2rem 0;

            label {
                font-weight: bold;
                display: block;
            }

            select {
                width: 100%;

                @include bp-min-mid {
                    width: auto;
                    min-width: 350px;
                }
            }
        }

        @include element('info') {
            strong {
                display: inline-block;
                margin-right: 0.5rem;
            }
        }
    }

    @include element('cta') {
        float: none !important;
        width: 100%;
        min-width: 140px;

        @include bp-min-mid {
            width: auto;
        }
    }

    @include element('waiting') {
        font-size: 4em;
        padding: 0.5em;
        min-height: 100px;

        @include bp-min-small {
            min-height: #{$inner-wizard-container-min-height - 120px};
        }
    }
}

</style>
